import { useEffect, useMemo, useState, type ChangeEvent, type InputHTMLAttributes } from 'react';
import {
  formatNationalPhoneDisplay,
  limitNationalDigits,
  nationalToE164,
  parseE164Phone,
  PHONE_COUNTRIES,
  phoneCountrySelectLabel,
  type PhoneCountryId,
} from '../../lib/intlPhone';

type IntlPhoneFieldProps = Omit<
  InputHTMLAttributes<HTMLInputElement>,
  'value' | 'onChange' | 'type' | 'inputMode'
> & {
  value: string;
  onChange: (e164OrEmpty: string) => void;
  defaultCountry?: PhoneCountryId;
  inputClassName?: string;
};

export function IntlPhoneField({
  value,
  onChange,
  defaultCountry = 'SG',
  inputClassName = 'field-input',
  className,
  disabled,
  id,
  name,
  required,
  'aria-label': ariaLabel,
}: IntlPhoneFieldProps) {
  const parsed = useMemo(() => parseE164Phone(value, defaultCountry), [value, defaultCountry]);
  const [countryId, setCountryId] = useState<PhoneCountryId>(parsed.countryId);
  const [display, setDisplay] = useState(() =>
    formatNationalPhoneDisplay(parsed.countryId, parsed.nationalDigits),
  );

  useEffect(() => {
    const next = parseE164Phone(value, defaultCountry);
    setCountryId(next.countryId);
    setDisplay(formatNationalPhoneDisplay(next.countryId, next.nationalDigits));
  }, [value, defaultCountry]);

  const country = PHONE_COUNTRIES.find((c) => c.id === countryId) ?? PHONE_COUNTRIES[0]!;

  const commit = (nextCountryId: PhoneCountryId, rawDisplay: string) => {
    const national = limitNationalDigits(nextCountryId, rawDisplay);
    const formatted = formatNationalPhoneDisplay(nextCountryId, national);
    setDisplay(formatted);
    onChange(nationalToE164(nextCountryId, national));
  };

  const handleCountryChange = (event: ChangeEvent<HTMLSelectElement>) => {
    const nextCountryId = event.target.value as PhoneCountryId;
    setCountryId(nextCountryId);
    const national = limitNationalDigits(nextCountryId, display);
    commit(nextCountryId, national);
  };

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    commit(countryId, event.target.value);
  };

  return (
    <div className={['intl-phone-field', className].filter(Boolean).join(' ')}>
      <select
        className="intl-phone-field__country"
        value={countryId}
        onChange={handleCountryChange}
        disabled={disabled}
        aria-label={`Country code (${country.name})`}
        title={country.name}
      >
        {PHONE_COUNTRIES.map((item) => (
          <option key={item.id} value={item.id} title={item.name}>
            {phoneCountrySelectLabel(item)}
          </option>
        ))}
      </select>
      <input
        id={id}
        name={name}
        type="tel"
        inputMode="tel"
        autoComplete="tel-national"
        className={`intl-phone-field__input ${inputClassName}`}
        value={display}
        onChange={handleInputChange}
        disabled={disabled}
        required={required}
        aria-label={ariaLabel ?? `Phone number (${country.label})`}
      />
    </div>
  );
}
